/**
 * ProtectedRoute 컴포넌트
 *
 * **역할:**
 * - 특정 라우트를 감싸서 권한이 있는 사용자만 접근할 수 있도록 보호하는 컴포넌트
 * - URL을 직접 입력해서 접근하는 경우에도 권한을 체크하여 차단
 *
 * **Sidebar와의 관계:**
 * - Sidebar: 메뉴를 "보여줄지 여부"를 제어 (권한 없는 메뉴는 숨김)
 * - ProtectedRoute: "실제 접근 허용 여부"를 제어 (URL 직접 입력 방어)
 * - 둘 다 RBAC 정책(rbac.ts의 canAccessRoute)을 기반으로 동작하며, 서로 보완 관계
 *
 * **동작 방식:**
 * 1. 사용자가 보호된 라우트(/hr/employees)에 접근 시도
 * 2. useAuthContext로 현재 사용자의 권한 정보 가져오기
 * 3. 권한 로딩 중이면 → 로딩 UI 표시
 * 4. 권한 로딩 완료 후:
 *    - canAccessRoute(authContext, path)로 접근 권한 확인
 *    - 권한 있음 → children (원래 페이지) 렌더링
 *    - 권한 없음 → ForbiddenPage (403) 표시
 *
 * **사용 예시:**
 * ```tsx
 * // App.tsx에서
 * <Route
 *   path="/hr/employees"
 *   element={
 *     <ProtectedRoute path="/hr/employees">
 *       <HrEmployeesPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */

import React from 'react';
import { Loader } from 'lucide-react';
import { useAuthContext } from '../shared/hooks/useAuthContext';
import { useIsOrgApprover } from '../shared/hooks/useIsOrgApprover'; // V168: 조직관리 승인자 확인
import { canAccessRoute } from '../utils/rbac';
import { ForbiddenPage } from '../pages/common/ForbiddenPage';

/**
 * ProtectedRoute Props 타입 정의
 */
export interface ProtectedRouteProps {
  /**
   * 권한 체크에 사용할 라우트 경로
   *
   * **주의:**
   * - ROUTE_PERMISSIONS에 정의된 경로와 일치해야 합니다.
   * - 동적 세그먼트가 있는 경우 (예: /hr/employees/:employeeId),
   *   권한 모델 상 동일하게 처리한다면 기본 경로(/hr/employees)로 통일해도 됩니다.
   *
   * **예시:**
   * - /hr/employees → "인사관리 > 직원관리" 권한 체크
   * - /hr/employees/:employeeId → 동일하게 "인사관리 > 직원관리" 권한 체크
   */
  path: string;

  /**
   * 보호할 컴포넌트 (권한이 있을 때 렌더링)
   */
  children: React.ReactNode;
}

/**
 * ProtectedRoute 컴포넌트
 *
 * @param path 권한 체크에 사용할 라우트 경로
 * @param children 보호할 컴포넌트
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ path, children }) => {
  // 현재 사용자의 AuthContext 가져오기
  const authContext = useAuthContext();

  // V168: 조직관리 승인자 여부 확인
  const { isOrgApprover, isLoading: isOrgApproverLoading } = useIsOrgApprover();

  // 1. 권한 정보 로딩 중 상태 처리
  if (!authContext.isLoaded || isOrgApproverLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        gap: '16px',
      }}>
        <Loader
          size={48}
          style={{
            color: 'var(--color-primary)',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{
          fontSize: '16px',
          color: 'var(--color-gray-600)',
        }}>
          권한 정보를 확인하는 중...
        </p>
      </div>
    );
  }

  // 2. 접근 권한 체크
  let canAccess = canAccessRoute(authContext, path);

  // V169: 승인관리 페이지는 SSoT 규칙에 따른 승인자도 접근 가능
  if (path === '/work/approvals' && isOrgApprover) {
    canAccess = true;
  }

  // 3. 권한이 없는 경우 → ForbiddenPage 표시
  if (!canAccess) {
    return <ForbiddenPage />;
  }

  // 4. 권한이 있는 경우 → 원래 페이지(children) 렌더링
  return <>{children}</>;
};

/**
 * ============================================
 * DEMO 모드에서의 동작
 * ============================================
 *
 * **DEMO 모드 (Firebase 미초기화):**
 * - useAuthContext가 자동으로 super_admin 권한 부여
 * - canAccessRoute가 모든 라우트에 대해 true 반환
 * - 결과: 모든 페이지 접근 가능
 *
 * **실제 Firebase 연동 환경:**
 * - useAuthContext가 Firestore에서 사용자의 실제 역할 할당 정보 조회
 * - canAccessRoute가 RBAC 정책에 따라 권한 확인
 * - 결과:
 *   - hr 또는 super_admin → 직원관리 페이지 접근 가능
 *   - general, tl, org_manager 등 → ForbiddenPage 표시
 *
 * **테스트 방법:**
 *
 * 1. DEMO 모드 (super_admin):
 *    - http://localhost:3001/hr/employees
 *    - 정상 접근 (HrEmployeesPage 표시)
 *
 * 2. 일반 사용자로 테스트하려면:
 *    - useAuthContext.ts에서 demoUserContext_general로 변경
 *    - http://localhost:3001/hr/employees
 *    - ForbiddenPage 표시 확인
 *
 * 3. Sidebar 메뉴 확인:
 *    - 권한 없는 사용자는 "직원관리" 메뉴 자체가 보이지 않음
 *    - URL 직접 입력 시에만 ForbiddenPage 노출
 */

/**
 * ============================================
 * 동적 라우트 처리 (/:id 형태)
 * ============================================
 *
 * **문제:**
 * - /hr/employees와 /hr/employees/:employeeId는 서로 다른 경로
 * - ROUTE_PERMISSIONS에서 어떻게 관리할지 결정 필요
 *
 * **해결 방법 1: 기본 경로로 통일**
 * - 직원 목록과 상세 모두 "인사관리 > 직원관리" 권한으로 통일
 * - ProtectedRoute의 path를 "/hr/employees"로 고정
 * - 예시:
 *   ```tsx
 *   <Route path="/hr/employees/:employeeId" element={
 *     <ProtectedRoute path="/hr/employees">
 *       <EmployeeDetailPage />
 *     </ProtectedRoute>
 *   } />
 *   ```
 *
 * **해결 방법 2: 별도 경로로 관리 (향후)**
 * - ROUTE_PERMISSIONS에 "/hr/employees/:employeeId" 추가
 * - 더 세밀한 권한 제어 가능 (예: 상세는 보기만, 목록은 관리)
 *
 * **현재 구현:**
 * - 방법 1 사용 (기본 경로로 통일)
 * - 직원 목록과 상세 모두 동일한 권한 체크
 */

// Loader 애니메이션을 위한 스타일 추가 (HrEmployeesPage와 동일)
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(style);
